import { createAvatar } from '@dicebear/core';
import type { Style } from '@dicebear/core';
import { bottts, icons } from '@dicebear/collection';
export { bottts, icons };
import SHA256 from 'crypto-js/sha256';
import { getSessionTokenV3 } from 'helpers/sessionContext';


const getBaseUrl = () => process.env.REACT_APP_API_V3;

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${getSessionTokenV3()}`,
});

// Helper to throw robust errors with status
const throwIfNotOk = async (response: Response, context: string) => {
    if (!response.ok) {
        const error: any = new Error(`Failed to ${context}: ${response.statusText}`);
        error.response = {
            status: response.status,
            statusText: response.statusText
        };
        try {
            // Attempt to parse any error details from JSON
            const json = await response.json();
            error.response.data = json;
        } catch (e) { /* ignore json parse errors */ }
        throw error;
    }
};

// ========================
// TEAM BRANDING
// ========================

export interface TeamBrandingData {
    id: string;
    name: string;
    color?: string;
    emoji?: string;
    avatar_url?: string;
    logo_dark_url?: string;
    logo_light_url?: string;
    role?: string;
    my_role?: string;
}

export interface TeamFeatures {
    business?: boolean | string;
    [key: string]: any;
}

/**
 * Fetch team details for branding
 */
export const fetchTeamDetails = async (teamId: string): Promise<TeamBrandingData> => {
    const response = await fetch(`${getBaseUrl()}/teams/${teamId}`, {
        headers: getAuthHeaders(),
    });

    await throwIfNotOk(response, 'fetch team details');

    const json = await response.json();
    return json.data || json;
};

/**
 * Fetch team features
 */
export const fetchTeamFeatures = async (teamId: string): Promise<TeamFeatures> => {
    const response = await fetch(`${getBaseUrl()}/teams/${teamId}/features`, {
        headers: getAuthHeaders(),
    });

    await throwIfNotOk(response, 'fetch team features');

    const json = await response.json();
    console.log('Features fetch response:', json);
    return json.data || json;
};

/**
 * Update team color
 */
export const updateTeamColor = async (teamId: string, color: string): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/teams/${teamId}/assets/color.primary.txt`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'text/plain',
        },
        body: color,
    });

    await throwIfNotOk(response, 'update team color');
};

/**
 * Update team details (name and emoji)
 */
export const updateTeamDetails = async (teamId: string, data: { name: string, emoji?: string | null }): Promise<void> => {

    // Ensure emoji is null if empty string, per backend requirement
    const payload = {
        name: data.name,
        emoji: data.emoji === '' ? null : data.emoji
    };

    const response = await fetch(`${getBaseUrl()}/teams/${teamId}`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    await throwIfNotOk(response, 'update team details');
};

/**
 * Update team avatar
 */
export const updateTeamAvatar = async (teamId: string, file: File): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/teams/${teamId}/assets/avatar`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'image/webp',
        },
        body: file,
    });

    await throwIfNotOk(response, 'update team avatar');
};

/**
 * Update team dark logo
 */
export const updateTeamLogoDark = async (teamId: string, file: File): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/teams/${teamId}/assets/logo.dark`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'image/webp',
        },
        body: file,
    });

    await throwIfNotOk(response, 'update team dark logo');
};

/**
 * Update team light logo
 */
export const updateTeamLogoLight = async (teamId: string, file: File): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/teams/${teamId}/assets/logo.light`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'image/webp',
        },
        body: file,
    });

    await throwIfNotOk(response, 'update team light logo');
};

// ========================
// WORKSPACE BRANDING
// ========================

export interface WorkspaceBrandingData {
    id: string;
    name: string;
    color?: string;
    avatar_url?: string;
    logo_dark_url?: string;
    logo_light_url?: string;
    role?: string;
    my_role?: string;
}

export interface WorkspaceFeatures {
    business?: boolean | string;
    [key: string]: any;
}

/**
 * Fetch workspace details for branding
 */
export const fetchWorkspaceDetails = async (workspaceId: string): Promise<WorkspaceBrandingData> => {
    const response = await fetch(`${getBaseUrl()}/workspaces/${workspaceId}`, {
        headers: getAuthHeaders(),
    });

    await throwIfNotOk(response, 'fetch workspace details');

    const json = await response.json();
    return json.data || json;
};

/**
 * Fetch workspace features
 */
export const fetchWorkspaceFeatures = async (workspaceId: string): Promise<WorkspaceFeatures> => {
    const response = await fetch(`${getBaseUrl()}/workspaces/${workspaceId}/features`, {
        headers: getAuthHeaders(),
    });

    await throwIfNotOk(response, 'fetch workspace features');

    const json = await response.json();
    return json.data || json;
};

/**
 * Update workspace color
 */
export const updateWorkspaceColor = async (workspaceId: string, color: string): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/workspaces/${workspaceId}/assets/color.primary.txt`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'text/plain',
        },
        body: color,
    });

    await throwIfNotOk(response, 'update workspace color');
};

/**
 * Update workspace avatar
 */
export const updateWorkspaceAvatar = async (workspaceId: string, file: File): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/workspaces/${workspaceId}/assets/avatar`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'image/webp',
        },
        body: file,
    });

    await throwIfNotOk(response, 'update workspace avatar');
};

/**
 * Update workspace dark logo
 */
export const updateWorkspaceLogoDark = async (workspaceId: string, file: File): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/workspaces/${workspaceId}/assets/logo.dark`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'image/webp',
        },
        body: file,
    });

    await throwIfNotOk(response, 'update workspace dark logo');
};

/**
 * Update workspace light logo
 */
export const updateWorkspaceLogoLight = async (workspaceId: string, file: File): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/workspaces/${workspaceId}/assets/logo.light`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'image/webp',
        },
        body: file,
    });

    await throwIfNotOk(response, 'update workspace light logo');
};

// ========================
// PERSONAL BRANDING
// ========================

export interface PersonalBrandingData {
    id: string;
    name: string;
    email: string;
    avatar_url?: string;
    rfc?: string;
    curp?: string;
    job?: string;
}

/**
 * Returns the raw URL for a user's avatar asset.
 */
export const getUserAvatarUrl = (userId: string | number) => {
    return `${getBaseUrl()}/users/${userId}/assets/avatar`;
};

/**
 * Returns the raw URL for a team's avatar asset.
 */
export const getTeamAvatarUrl = (teamId: string | number) => {
    return `${getBaseUrl()}/teams/${teamId}/assets/avatar`;
};

/**
 * Returns the default placeholder for a team's avatar.
 */
export const getDefaultTeamAvatarUrl = () => {
    return '/img/defaultOrg.png';
};

/**
 * Returns the default placeholder for a user's avatar.
 */
export const getDefaultUserAvatarUrl = () => {
    return '/img/user_placeholder.png';
};

/**
 * Generates a branded Dicebear avatar for guest users.
 */
export const generateGuestUserAvatar = (email: string, primaryColor: string) => {
    const cleanEmail = email.toLowerCase().trim();
    const avatarSeed = SHA256(cleanEmail).toString();

    return generateUserDefaultAvatar(avatarSeed, primaryColor);
};

/**
 * Generates a branded Dicebear avatar for guest teams.
 */
export const generateGuestTeamAvatar = (primaryColor: string) => {
    return generateTeamDefaultAvatar('guest-team', primaryColor);
};

/**
 * Generates the default Dicebear avatar for teams.
 */
export const generateTeamDefaultAvatar = (seed: string, color: string) => {
    const colorHex = (color || '#3182ce').replace('#', '');
    return createAvatar(icons, {
        seed,
        backgroundColor: [colorHex],
        backgroundType: ['gradientLinear'],
    }).toDataUri();
};

/**
 * Generates the default Dicebear avatar for users.
 */
export const generateUserDefaultAvatar = (seed: string, color: string) => {
    const colorHex = (color || '#3182ce').replace('#', '');
    return createAvatar(bottts, {
        seed,
        baseColor: [colorHex],
        eyes: ['eva'],
        face: ['round02'],
    }).toDataUri();
};

/**
 * Generic Dicebear avatar generator wrapper.
 */
export const generateDicebearAvatar = (style: Style<any>, options: any = {}) => {
    return createAvatar(style, options);
};

/**
 * Update personal profile details
 */
export const updatePersonalDetails = async (userId: string, data: { name?: string, rfc?: string, curp?: string, job?: string }): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/users/${userId}`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    await throwIfNotOk(response, 'update personal details');
};

/**
 * Update personal avatar
 */
export const updatePersonalAvatar = async (userId: string, file: File): Promise<void> => {
    const response = await fetch(`${getBaseUrl()}/users/${userId}/assets/avatar`, {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'image/webp',
        },
        body: file,
    });

    await throwIfNotOk(response, 'update personal avatar');
};

// ========================
// HELPER: Extract file from form value
// ========================

/**
 * Extracts a File object from various form input formats
 */
export const extractFile = (value: any): File | null => {
    if (!value) return null;
    if (value instanceof File) return value;
    if (value instanceof FileList && value.length > 0) return value[0];
    if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) return value[0];
    return null;
};

// ========================
// ASSET FETCHING HELPER
// ========================

const fetchAssetBlobUrl = async (url: string): Promise<string | null> => {
    try {
        const response = await fetch(url, { headers: getAuthHeaders() });
        if (!response.ok) return null;
        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (e) {
        console.error('Failed to fetch asset:', url, e);
        return null; // Return null on failure so UI can fallback or show nothing
    }
};

const fetchAssetText = async (url: string): Promise<string | null> => {
    try {
        const response = await fetch(url, { headers: getAuthHeaders() });
        if (!response.ok) return null;
        return await response.text();
    } catch (e) {
        console.error('Failed to fetch text asset:', url, e);
        return null; // Return null on failure so UI can fallback or show nothing
    }
};

export const fetchTeamAssets = async (teamId: string) => {
    const baseUrl = `${getBaseUrl()}/teams/${teamId}/assets`;
    const ts = Math.floor(new Date().getTime() / 1000); // 1-second resolution cache busting
    const [avatar, logoDark, logoLight, color] = await Promise.all([
        fetchAssetBlobUrl(`${baseUrl}/avatar?t=${ts}`),
        fetchAssetBlobUrl(`${baseUrl}/logo.dark?t=${ts}`),
        fetchAssetBlobUrl(`${baseUrl}/logo.light?t=${ts}`),
        fetchAssetText(`${baseUrl}/color.primary.txt?t=${ts}`)
    ]);
    return { avatar, logoDark, logoLight, color };
};

export const fetchWorkspaceAssets = async (workspaceId: string) => {
    const baseUrl = `${getBaseUrl()}/workspaces/${workspaceId}/assets`;
    const ts = Math.floor(new Date().getTime() / 1000);
    const [avatar, logoDark, logoLight, color] = await Promise.all([
        fetchAssetBlobUrl(`${baseUrl}/avatar?t=${ts}`),
        fetchAssetBlobUrl(`${baseUrl}/logo.dark?t=${ts}`),
        fetchAssetBlobUrl(`${baseUrl}/logo.light?t=${ts}`),
        fetchAssetText(`${baseUrl}/color.primary.txt?t=${ts}`)
    ]);
    return { avatar, logoDark, logoLight, color };
};

export const fetchPersonalAssets = async (userId: string) => {
    const baseUrl = `${getBaseUrl()}/users/${userId}/assets`;
    const ts = Math.floor(new Date().getTime() / 1000);
    // Users currently only manage their avatar as a specialized asset in this flow
    const [avatar] = await Promise.all([
        fetchAssetBlobUrl(`${baseUrl}/avatar?t=${ts}`)
    ]);
    return { avatar };
};
